import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import Logo from '@/components/Logo';
import { useLanguage } from '@/contexts/LanguageContext';

export default function Register() {
  const { currentLang } = useLanguage();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    tenant_name: '',
    tenant_subdomain: '',
    subscription_plan: 'basic', // basic, pro, full, enterprise
    billing_period: 'monthly', // monthly, annual
    card_number: '',
    card_expiry: '',
    card_cvv: '',
    card_holder: '',
    preferred_language: currentLang || 'it'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  
  // Get selected plan from sessionStorage (from landing page)
  useEffect(() => {
    const selectedPlan = sessionStorage.getItem('selected_plan');
    if (selectedPlan) {
      const planMap = {
        'Basic': 'basic',
        'Pro': 'pro',
        'Full': 'full',
        'Enterprise': 'enterprise'
      };
      setFormData(prev => ({ 
        ...prev, 
        subscription_plan: planMap[selectedPlan] || 'basic' 
      }));
    }
  }, []);
  
  // Update preferred_language when currentLang changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, preferred_language: currentLang || 'it' }));
  }, [currentLang]);
  
  const plans = {
    basic: { name: 'Basic', price: 0, features: '20 partecipanti, 1 scanner' },
    pro: { name: 'Pro', priceMonthly: 25, priceAnnual: 285, features: '100 partecipanti, 3 scanner, eventi online' },
    full: { name: 'Full', priceMonthly: 50, priceAnnual: 570, features: '500 partecipanti, 6 scanner' },
    enterprise: { name: 'Enterprise', price: 'Custom', features: 'Illimitato' }
  };
  
  const selectedPlan = plans[formData.subscription_plan];
  const needsPayment = formData.subscription_plan !== 'basic' && formData.subscription_plan !== 'enterprise';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!privacyAccepted || !termsAccepted) {
      toast.error('Devi accettare Privacy Policy e Termini di Servizio');
      return;
    }
    
    setLoading(true);
    try {
      await register(formData);
      toast.success('Registrazione completata!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-4">
          <img 
            src="/zonepass-logo-final.png" 
            alt="ZonePass Logo" 
            className="h-40 md:h-48 lg:h-56 w-auto mx-auto mb-2"
          />
          <p className="text-gray-600 text-sm mb-3">Crea il tuo account gratuitamente</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h2 className="text-2xl font-semibold mb-6" data-testid="register-title">Registrazione</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="full_name">Nome Completo</Label>
              <Input
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                data-testid="register-name-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="register-email-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="register-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="tenant_name">Nome Organizzazione</Label>
              <Input
                id="tenant_name"
                name="tenant_name"
                value={formData.tenant_name}
                onChange={handleChange}
                required
                data-testid="register-org-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="tenant_subdomain">Subdomain (es: miaorg)</Label>
              <Input
                id="tenant_subdomain"
                name="tenant_subdomain"
                value={formData.tenant_subdomain}
                onChange={handleChange}
                required
                data-testid="register-subdomain-input"
                className="mt-1"
                placeholder="miaorganizzazione"
              />
            </div>
            
            {/* Selected Plan Display */}
            <div className="border-t pt-4 mt-6">
              <Label className="text-lg font-semibold">Piano Selezionato</Label>
              <div className="mt-3">
                <div className="flex items-start p-4 border-2 rounded-lg border-purple-600 bg-purple-50">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">{selectedPlan.name}</span>
                      <span className="text-purple-600 font-bold">
                        {selectedPlan.price === 0 ? 'Gratis' : 
                         selectedPlan.price === 'Custom' ? 'Custom' : 
                         formData.billing_period === 'monthly' 
                           ? `€${selectedPlan.priceMonthly}/mese` 
                           : `€${selectedPlan.priceAnnual}/anno`}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{selectedPlan.features}</p>
                  </div>
                </div>
                {formData.subscription_plan !== 'basic' && (
                  <p className="text-xs text-gray-500 mt-2">
                    Vuoi cambiare piano? <Link to="/#pricing" className="text-purple-600 hover:underline">Torna alla pagina prezzi</Link>
                  </p>
                )}
              </div>
              
              {/* Billing Period for paid plans */}
              {needsPayment && (
                <div className="mt-4">
                  <Label>Fatturazione</Label>
                  <div className="mt-2 flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="billing_period"
                        value="monthly"
                        checked={formData.billing_period === 'monthly'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <span>Mensile</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="billing_period"
                        value="annual"
                        checked={formData.billing_period === 'annual'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <span>Annuale (5% sconto)</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
            
            {/* Payment Info - only for paid plans */}
            {needsPayment && (
              <div className="border-t pt-4 mt-6 space-y-4">
                <Label className="text-lg font-semibold">Informazioni Pagamento</Label>
                <div>
                  <Label htmlFor="card_holder">Intestatario Carta</Label>
                  <Input
                    id="card_holder"
                    name="card_holder"
                    value={formData.card_holder}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="Mario Rossi"
                  />
                </div>
                <div>
                  <Label htmlFor="card_number">Numero Carta</Label>
                  <Input
                    id="card_number"
                    name="card_number"
                    value={formData.card_number}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="card_expiry">Scadenza (MM/AA)</Label>
                    <Input
                      id="card_expiry"
                      name="card_expiry"
                      value={formData.card_expiry}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="12/25"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <Label htmlFor="card_cvv">CVV</Label>
                    <Input
                      id="card_cvv"
                      name="card_cvv"
                      type="password"
                      value={formData.card_cvv}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="123"
                      maxLength={4}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  🔒 I tuoi dati di pagamento sono protetti e criptati. Il primo addebito avverrà alla scadenza del primo periodo (mensile o annuale).
                </p>
              </div>
            )}
            
            <div className="space-y-3 pt-4">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="privacy"
                  checked={privacyAccepted}
                  onCheckedChange={setPrivacyAccepted}
                  data-testid="privacy-checkbox"
                  className="mt-1"
                />
                <label htmlFor="privacy" className="text-sm text-gray-700 leading-tight cursor-pointer">
                  Accetto l'<Link to="/privacy" target="_blank" className="text-blue-600 hover:underline font-medium">Informativa sulla Privacy</Link> e il trattamento dei dati personali secondo GDPR
                </label>
              </div>
              
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  data-testid="terms-checkbox"
                  className="mt-1"
                />
                <label htmlFor="terms" className="text-sm text-gray-700 leading-tight cursor-pointer">
                  Accetto i <Link to="/terms" target="_blank" className="text-blue-600 hover:underline font-medium">Termini e Condizioni del Servizio</Link>
                </label>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !privacyAccepted || !termsAccepted}
              data-testid="register-submit-button"
            >
              {loading ? 'Registrazione...' : 'Registrati'}
            </Button>
          </form>
          <p className="text-center mt-6 text-sm text-gray-600">
            Hai già un account?{' '}
            <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="login-link">
              Accedi
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
